import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useRegistration } from '../../context/RegistrationContext';
import Logo from '../common/Logo';
import PartyDonationModal from '../common/PartyDonationModal';
import LanguageSwitcher from '../common/LanguageSwitcher';

const Header = () => {
  const { t } = useTranslation();
  const { isAuthenticated, isAdmin, isCandidate, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { registrationStep } = useRegistration();
  const [showPartyDonateModal, setShowPartyDonateModal] = useState(false);
  const [showNavigationConfirmModal, setShowNavigationConfirmModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Only show modal on register page if user is past step 1 (Email tab)
  const shouldShowConfirmModal = location.pathname === '/register' && registrationStep > 1;

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  const handleNavClick = (e, path) => {
    // Only show modal if:
    // - User is on register page AND past step 1 (Email tab)
    // - Trying to navigate to a different page
    if (shouldShowConfirmModal && path !== location.pathname) {
      e.preventDefault();
      setPendingNavigation(path);
      setShowNavigationConfirmModal(true);
    }
    setMobileMenuOpen(false);
  };

  const handleConfirmNavigation = () => {
    setShowNavigationConfirmModal(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const handleCancelNavigation = () => {
    setShowNavigationConfirmModal(false);
    setPendingNavigation(null);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm border-b border-gray-100">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" onClick={(e) => handleNavClick(e, '/')} className="flex items-center hover:opacity-80 transition-opacity">
            <Logo size="md" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-4">
            <Link to="/candidates" onClick={(e) => handleNavClick(e, '/candidates')} className="text-gray-700 hover:text-primary-600 font-medium">
              {t('nav.candidates')}
            </Link>
            <Link to="/donations" onClick={(e) => handleNavClick(e, '/donations')} className="text-gray-700 hover:text-primary-600 font-medium">
              {t('nav.donations')}
            </Link>
            <Link to="/reports" onClick={(e) => handleNavClick(e, '/reports')} className="text-gray-700 hover:text-primary-600 font-medium">
              {t('nav.reports')}
            </Link>
            <button
              onClick={() => setShowPartyDonateModal(true)}
              className="text-primary-600 hover:text-primary-700 font-semibold flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {t('nav.donateToParty')}
            </button>

            {isAuthenticated() ? (
              <>
                {isAdmin() && (
                  <Link to="/admin" onClick={(e) => handleNavClick(e, '/admin')} className="text-gray-700 hover:text-primary-600 font-medium">
                    {t('nav.admin')}
                  </Link>
                )}
                <Link to="/profile" onClick={(e) => handleNavClick(e, '/profile')} className="text-gray-700 hover:text-primary-600 font-medium">
                  {t('nav.profile')}
                </Link>
                {!isCandidate() && (
                  <Link to="/my-donations" onClick={(e) => handleNavClick(e, '/my-donations')} className="text-gray-700 hover:text-primary-600 font-medium">
                    {t('nav.myDonations')}
                  </Link>
                )}
                {isCandidate() && (
                  <Link to="/candidate/my-profile" onClick={(e) => handleNavClick(e, '/candidate/my-profile')} className="text-gray-700 hover:text-primary-600 font-medium">
                    My Candidate Profile
                  </Link>
                )}
                <LanguageSwitcher />
                <button
                  onClick={handleLogout}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-button hover:bg-gray-300 transition"
                >
                  {t('nav.logout')}
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={(e) => handleNavClick(e, '/login')}
                  className="bg-secondary-500 text-white px-5 py-2 rounded-button hover:bg-secondary-600 transition font-medium"
                >
                  {t('nav.login')}
                </Link>
                <LanguageSwitcher />
                {/* <Link
                  to="/register"
                  onClick={(e) => handleNavClick(e, '/register')}
                  className="bg-primary-500 text-white px-5 py-2 rounded-button hover:bg-primary-600 transition font-medium"
                >
                  {t('nav.register')}
                </Link> */}
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-100 animate-fadeIn">
            <div className="flex flex-col space-y-3">
              <Link
                to="/candidates"
                onClick={(e) => handleNavClick(e, '/candidates')}
                className="text-gray-700 hover:text-primary-600 font-medium py-2 px-3 rounded-lg hover:bg-gray-50 transition"
              >
                {t('nav.candidates')}
              </Link>
              <Link
                to="/donations"
                onClick={(e) => handleNavClick(e, '/donations')}
                className="text-gray-700 hover:text-primary-600 font-medium py-2 px-3 rounded-lg hover:bg-gray-50 transition"
              >
                {t('nav.donations')}
              </Link>
              <Link
                to="/reports"
                onClick={(e) => handleNavClick(e, '/reports')}
                className="text-gray-700 hover:text-primary-600 font-medium py-2 px-3 rounded-lg hover:bg-gray-50 transition"
              >
                {t('nav.reports')}
              </Link>
              <button
                onClick={() => {
                  setShowPartyDonateModal(true);
                  closeMobileMenu();
                }}
                className="text-primary-600 hover:text-primary-700 font-semibold flex items-center py-2 px-3 rounded-lg hover:bg-primary-50 transition text-left"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('nav.donateToParty')}
              </button>

              {isAuthenticated() ? (
                <>
                  {isAdmin() && (
                    <Link
                      to="/admin"
                      onClick={(e) => handleNavClick(e, '/admin')}
                      className="text-gray-700 hover:text-primary-600 font-medium py-2 px-3 rounded-lg hover:bg-gray-50 transition"
                    >
                      {t('nav.admin')}
                    </Link>
                  )}
                  <Link
                    to="/profile"
                    onClick={(e) => handleNavClick(e, '/profile')}
                    className="text-gray-700 hover:text-primary-600 font-medium py-2 px-3 rounded-lg hover:bg-gray-50 transition"
                  >
                    {t('nav.profile')}
                  </Link>
                  {!isCandidate() && (
                    <Link
                      to="/my-donations"
                      onClick={(e) => handleNavClick(e, '/my-donations')}
                      className="text-gray-700 hover:text-primary-600 font-medium py-2 px-3 rounded-lg hover:bg-gray-50 transition"
                    >
                      {t('nav.myDonations')}
                    </Link>
                  )}
                  {isCandidate() && (
                    <Link
                      to="/candidate/my-profile"
                      onClick={(e) => handleNavClick(e, '/candidate/my-profile')}
                      className="text-gray-700 hover:text-primary-600 font-medium py-2 px-3 rounded-lg hover:bg-gray-50 transition"
                    >
                      My Candidate Profile
                    </Link>
                  )}
                  <div className="py-2 px-3">
                    <LanguageSwitcher />
                  </div>
                  <button
                    onClick={handleLogout}
                    className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition font-medium text-left"
                  >
                    {t('nav.logout')}
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={(e) => handleNavClick(e, '/login')}
                    className="text-gray-700 hover:text-primary-600 font-medium py-2 px-3 rounded-lg hover:bg-gray-50 transition"
                  >
                    {t('nav.login')}
                  </Link>
                  <div className="py-2 px-3">
                    <LanguageSwitcher />
                  </div>
                  {/* <Link
                    to="/register"
                    onClick={(e) => handleNavClick(e, '/register')}
                    className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition font-medium text-center"
                  >
                    {t('nav.register')}
                  </Link> */}
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Party Donation Modal */}
      <PartyDonationModal
        isOpen={showPartyDonateModal}
        onClose={() => setShowPartyDonateModal(false)}
      />

      {/* Navigation Confirmation Modal */}
      {showNavigationConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 animate-fadeIn">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900">{t('modals.leavePageTitle')}</h3>
            </div>

            <p className="text-gray-600 mb-6">
              {t('modals.leavePageMessage')}
            </p>

            <div className="flex gap-3">
              <button
                onClick={handleCancelNavigation}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-button font-semibold hover:bg-gray-200 transition"
              >
                {t('actions.stayHere')}
              </button>
              <button
                onClick={handleConfirmNavigation}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-button font-semibold hover:bg-red-700 transition"
              >
                {t('actions.leavePage')}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
